import React, { createContext, useContext, useMemo, useEffect } from 'react'

interface ThemeContextType {
  theme: 'light'
  setTheme: (t: 'light') => void
  toggle: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // always use light theme - dark mode removed
  const theme = 'light' as const
  
  // force light mode on mount and prevent dark mode
  useEffect(() => {
    const root = document.documentElement
    // remove dark class if it exists
    root.classList.remove('dark')
    // clear any saved theme preference
    localStorage.removeItem('seacure_theme')
  }, [])
  
  // no-op functions for compatibility
  const handleSetTheme = (_t: 'light') => {
    // theme is always light
  }
  
  const value = useMemo(() => ({ 
    theme, 
    setTheme: handleSetTheme, 
    toggle: () => {} // no-op
  }), [])
  
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  )
}
